import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FiCheck, FiFileText, FiArrowRight, FiUsers } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import QuoteModal from '../components/quote/QuoteModal';
import { SEO_LANDINGS, V3, landingLabel } from '../data/seoLandingPages';
import { useSettings } from '../context/SettingsContext';
import { stripDiscount } from '../utils/discountText';

const API = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = 'https://royalgardenvillas.in';
const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const bhk = (v) => `${v.bedrooms}BHK ${v.name}`;
const img = (v) => {
  const i = (v.images || [])[0] || '';
  return i.startsWith('http') ? i : `${ORIGIN}${i}`;
};

export const priceFor = (v, guests) => {
  const g = Math.min(Math.max(guests, 1), v.capacity);
  const extra = Math.max(0, g - (v.base_capacity || v.capacity)) * (v.extra_guest_charge || 1000);
  return { g, weekday: v.base_price + extra, weekend: v.weekend_price + extra };
};

const VillaCard = ({ v, guests, onQuote }) => {
  const p = priceFor(v, guests);
  return (
    <div data-testid={`landing-villa-${v.id}`} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex flex-col">
      <img src={img(v)} alt={`${bhk(v)} — private pool villa in Lonavala`} loading="lazy" className="w-full h-56 object-cover" />
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{bhk(v)}</h3>
        <p className="text-xs text-[#6B7280] mt-1 flex items-center gap-1"><FiUsers className="w-3.5 h-3.5" /> {v.base_capacity} guests included · up to {v.capacity} · {v.bedrooms} AC bedrooms · {v.bathrooms || v.bedrooms} washrooms</p>
        <div className="mt-4 bg-[#FFF7ED] rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-[#C2410C] font-bold">For {p.g} guests</p>
          <p className="text-2xl font-bold text-[#1A1A2E] mt-1" data-testid={`landing-price-${v.id}`}>{inr(p.weekday)}<span className="text-xs font-normal text-[#6B7280]"> /night weekday</span></p>
          <p className="text-xs text-[#6B7280]">≈ {inr(p.weekday / p.g)} per person · weekend {inr(p.weekend)}/night</p>
        </div>
        <ul className="mt-4 space-y-1.5 flex-1">
          {(v.amenities || []).slice(0, 6).map((a) => (
            <li key={a} className="text-sm text-[#374151] flex items-start gap-2"><FiCheck className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />{a}</li>
          ))}
        </ul>
        <div className="mt-5 flex gap-2">
          <Link to={`/villas/${v.id}`} data-testid={`landing-view-${v.id}`} className="flex-1 text-center bg-[#F57C20] hover:bg-[#E06A10] text-white text-sm font-semibold py-3 rounded-lg transition-colors">Check Availability</Link>
          <button type="button" onClick={() => onQuote(v.id)} data-testid={`landing-quote-${v.id}`} className="flex-1 border-2 border-[#1A1A2E] text-[#1A1A2E] hover:bg-[#1A1A2E] hover:text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Get Quote</button>
        </div>
      </div>
    </div>
  );
};

const CostTable = ({ villas, guests, pct }) => (
  <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-testid="landing-cost-table">
    <table className="w-full text-sm">
      <thead className="bg-[#1B2432] text-white">
        <tr>
          <th className="text-left px-4 py-3">Villa</th>
          <th className="text-left px-4 py-3">1 night (weekday)</th>
          <th className="text-left px-4 py-3">{pct ? `2 nights (2nd night ${pct}% off)` : '2 nights'}</th>
          <th className="text-left px-4 py-3">Per person / night (2-night avg)</th>
        </tr>
      </thead>
      <tbody>
        {villas.map((v) => {
          const p = priceFor(v, guests);
          const two = p.weekday + Math.round(p.weekday * (1 - (pct || 0) / 100));
          return (
            <tr key={v.id} className="border-b border-gray-100">
              <td className="px-4 py-3 font-semibold text-[#1A1A2E]">{v.bedrooms}BHK · {p.g} guests</td>
              <td className="px-4 py-3">{inr(p.weekday)}</td>
              <td className="px-4 py-3">{inr(two)}</td>
              <td className="px-4 py-3">{inr(two / 2 / p.g)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const GOOD_TO_KNOW = [
  'Check-in 1:00 PM · Check-out 10:00 AM',
  'Entire villa & private pool exclusive to your group',
  'BYOB allowed · order from Swiggy, Zomato or your caterer',
  'Add-ons: BBQ ₹3,000/meal · Bonfire ₹2,000 · Maid ₹2,000/day · Dedicated Cooking Space',
  'Housekeeping 10 AM – 6 PM · 24/7 security · gated compound',
  'Outdoor music till 10 PM · pets not allowed · 100% online payment',
];

export default function SeoLanding({ slug }) {
  const base = SEO_LANDINGS.find((p) => p.slug === slug);
  const { nightDiscountEnabled: dOn, nightDiscountPct: pct } = useSettings();
  const pctTxt = (t) => String(t).replace(/15%/g, `${pct}%`);
  const sd = (t) => stripDiscount(pctTxt(t), dOn);
  const page = {
    ...base,
    title: base.title, description: sd(base.description), intro: sd(base.intro),
    highlights: base.highlights.map(sd).filter(Boolean),
    faqs: base.faqs.filter((f) => dOn || !/discount|% off/i.test(f.q + f.a)).map((f) => ({ q: f.q, a: sd(f.a) })),
  };
  const [villas, setVillas] = useState([]);
  const [quoteVilla, setQuoteVilla] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/villas`).then((r) => setVillas(r.data || [])).catch(() => setVillas([]));
  }, []);
  const list = page.villas.map((id) => villas.find((v) => v.id === id)).filter(Boolean);
  const primary = list[0];
  const from = primary ? Math.min(...list.map((v) => priceFor(v, page.guests).weekday)) : null;
  const fill = (s) => s.replace('{price}', from ? from.toLocaleString('en-IN') : (villas.find((v) => v.id === V3)?.base_price || 10999).toLocaleString('en-IN'));
  const path = `/${page.slug}`;
  const jsonLd = list.length ? [{
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: page.h1,
    url: `${ORIGIN}${path}`,
    itemListElement: list.map((v, i) => {
      const p = priceFor(v, page.guests);
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'LodgingBusiness',
          '@id': `${ORIGIN}/villas/${v.id}`,
          name: bhk(v),
          url: `${ORIGIN}/villas/${v.id}`,
          image: img(v),
          telephone: '+91 98200 88292',
          priceRange: `${inr(v.base_price)} - ${inr(v.weekend_price)} per night`,
          numberOfRooms: v.bedrooms,
          occupancy: { '@type': 'QuantitativeValue', minValue: v.base_capacity, maxValue: v.capacity, unitText: 'guests' },
          address: { '@type': 'PostalAddress', streetAddress: 'Tata Dam Road, Tungarli', addressLocality: 'Lonavala', addressRegion: 'Maharashtra', postalCode: '410401', addressCountry: 'IN' },
          makesOffer: { '@type': 'Offer', price: p.weekday, priceCurrency: 'INR', description: `Weekday nightly rate for ${p.g} guests`, availability: 'https://schema.org/InStock' },
        },
      };
    }),
  }] : [];

  return (
    <div data-testid={`landing-${page.slug}`} className="pt-20 bg-[#F7F8FA]">
      <SEO title={fill(page.title)} description={fill(page.description)} path={path} image={primary ? img(primary) : undefined}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Villas', url: '/villas' }, { name: page.h1, url: path }]} jsonLd={jsonLd} />
      <section className="bg-[#1B2432] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
          <nav className="text-xs text-white/50 mb-5"><Link to="/" className="hover:text-white">Home</Link> / <Link to="/villas" className="hover:text-white">Villas</Link> / <span className="text-white/80">{landingLabel(page.slug)}</span></nav>
          <h1 data-testid="landing-h1" className="text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>{page.h1}</h1>
          <p data-speakable className="mt-5 text-white/75 max-w-3xl text-sm md:text-base leading-relaxed">{page.intro}</p>
          {from && <p data-testid="landing-from-price" className="mt-5 inline-block bg-[#F57C20] text-white text-sm font-semibold px-4 py-2 rounded-full">From {inr(from)}/night (weekday){dOn ? ` · ${pct}% off 2nd night onwards` : ''}</p>}
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 max-w-3xl">
            {page.highlights.map((h) => <li key={h} className="flex items-start gap-2 text-sm text-white/85"><FiCheck className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />{h}</li>)}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            {primary && <Link to={`/villas/${primary.id}`} data-testid="landing-hero-book" className="inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#E06A10] px-6 py-3 rounded-lg text-sm font-semibold transition-colors">Check Availability <FiArrowRight className="w-4 h-4" /></Link>}
            <button type="button" data-testid="landing-hero-quote" onClick={() => setQuoteVilla(primary?.id || '')} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-lg text-sm font-semibold transition-colors"><FiFileText className="w-4 h-4" /> Get Instant Quote</button>
            <a href={`https://wa.me/919820088292?text=${encodeURIComponent(`Hi, I'm looking for a villa in Lonavala for ${page.guests} guests.`)}`} target="_blank" rel="noopener noreferrer" data-testid="landing-hero-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] px-6 py-3 rounded-lg text-sm font-semibold transition-colors"><FaWhatsapp className="w-4 h-4" /> WhatsApp</a>
          </div>
          </div>
          {primary && <img src={img(primary)} alt={`${page.h1} — ${bhk(primary)}`} className="hidden lg:block lg:col-span-2 w-full h-[420px] object-cover rounded-2xl shadow-2xl" data-testid="landing-hero-img" />}
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <h2 className="text-2xl md:text-3xl text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Recommended villas for {page.guests} guests</h2>
        <div className={`grid gap-6 ${list.length > 2 ? 'md:grid-cols-3' : list.length === 2 ? 'md:grid-cols-2' : 'max-w-xl'}`}>
          {list.map((v) => <VillaCard key={v.id} v={v} guests={page.guests} onQuote={setQuoteVilla} />)}
        </div>
        {list.length > 0 && (
          <>
            <h2 className="text-2xl md:text-3xl text-[#1A1A2E] mt-14 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>What it costs for {page.guests} guests</h2>
            <CostTable villas={list} guests={page.guests} pct={dOn ? pct : 0} />
            <p className="text-xs text-[#6B7280] mt-3">Live weekday rates. Weekend (Fri–Sun) and festive dates are higher — see exact prices on the booking page. Extra guests above base capacity: ₹1,000 per person per night. Refundable security deposit ₹5,000.</p>
          </>
        )}
        <h2 className="text-2xl md:text-3xl text-[#1A1A2E] mt-14 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Good to know</h2>
        <ul className="grid sm:grid-cols-2 gap-3">
          {GOOD_TO_KNOW.map((g) => <li key={g} className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm text-[#374151] flex items-start gap-2"><FiCheck className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />{g}</li>)}
        </ul>
      </section>
      <FAQSection title={`FAQs — ${landingLabel(page.slug)}`} items={page.faqs} testid="landing-faq" />
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <h2 className="text-xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Related searches</h2>
        <div className="flex flex-wrap gap-2">
          {page.related.map((s) => <Link key={s} to={`/${s}`} data-testid={`landing-related-${s}`} className="bg-white border border-gray-200 hover:border-[#F57C20] hover:text-[#F57C20] rounded-full px-4 py-2 text-sm text-[#374151] transition-colors">{landingLabel(s)}</Link>)}
        </div>
      </section>
      <QuoteModal open={quoteVilla !== null} onClose={() => setQuoteVilla(null)} mode="guest" initial={{ villa_id: quoteVilla || '', guests: page.guests }} title={`Get Your Quote — ${page.guests} Guests`} />
    </div>
  );
}
